import config from '../../config/config';

/** Refresh when within 6 hours of idle expiry (25% of the 24-hour window). */
const REFRESH_BEFORE_MS = 6 * 60 * 60 * 1000;

/** At most one refresh attempt per 15 minutes unless forced. */
const MIN_REFRESH_INTERVAL_MS = 15 * 60 * 1000;

let sessionExpiresAtMs = null;
let lastRefreshAttemptMs = 0;
let refreshInFlight = null;

export function applySessionTiming(session) {
  if (!session?.expiresAt) {
    sessionExpiresAtMs = null;
    return;
  }
  const parsed = new Date(session.expiresAt).getTime();
  sessionExpiresAtMs = Number.isNaN(parsed) ? null : parsed;
}

export function clearSessionTiming() {
  sessionExpiresAtMs = null;
  lastRefreshAttemptMs = 0;
  refreshInFlight = null;
}

export function hasSessionTiming() {
  return sessionExpiresAtMs !== null;
}

/**
 * Extends the sliding session when the idle window is nearing expiry.
 * @param {boolean} [force]
 * @returns {Promise<boolean>}
 */
export async function maybeRefreshSession(force = false) {
  if (!sessionExpiresAtMs) return false;

  const now = Date.now();
  if (!force && now - lastRefreshAttemptMs < MIN_REFRESH_INTERVAL_MS) {
    return false;
  }
  if (!force && now < sessionExpiresAtMs - REFRESH_BEFORE_MS) {
    return false;
  }

  if (refreshInFlight) {
    return refreshInFlight;
  }

  lastRefreshAttemptMs = now;
  refreshInFlight = (async () => {
    try {
      const { authService } = await import('./authService');
      const session = await authService.refreshSession();
      applySessionTiming(session);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/** One silent refresh + retry path for expired idle windows. */
export async function tryRecoverSession() {
  try {
    const { authService } = await import('./authService');
    const session = await authService.refreshSession();
    applySessionTiming(session);
    return true;
  } catch {
    return false;
  }
}

export const sessionRefreshConfig = {
  pollIntervalMs: 5 * 60 * 1000,
  refreshBeforeMs: REFRESH_BEFORE_MS,
  minRefreshIntervalMs: MIN_REFRESH_INTERVAL_MS,
  refreshUrl: `${config.apimUrl}/authentication-api/Refresh`,
};
